import React, { useState, useEffect } from 'react';
import { Users, UserCheck, FileCheck, Banknote, Clock, XCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const fmt = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const monthLabel = (m) => {
  if (!m) return '';
  const d = new Date(m + '-01');
  return `${d.toLocaleString('en-IN', { month: 'long' })}, ${m.split('-')[0]}`;
};

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally { setLoading(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  const monthly = stats?.monthly_breakdown || [];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="mt-1 text-base text-slate-600">Welcome back, {user.name}</p>
        </div>
        <img src="/logo.png" alt="Mr. Chow's" className="h-20" data-testid="dashboard-logo" />
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard title="Total Employees" value={stats?.total_employees || 0} icon={Users} bg="bg-blue-50" text="text-blue-700" border="border-blue-200" />
        <StatCard title="Active Employees" value={stats?.active_employees || 0} icon={UserCheck} bg="bg-emerald-50" text="text-emerald-700" border="border-emerald-200" />
        <StatCard title="Pending Approvals" value={stats?.pending_approvals || 0} icon={FileCheck} bg="bg-amber-50" text="text-amber-700" border="border-amber-200" />
        <StatCard title="Pending Banking" value={stats?.pending_payments || 0} icon={Banknote} bg="bg-indigo-50" text="text-indigo-700" border="border-indigo-200" />
        <StatCard title="Rejected" value={stats?.rejected_count || 0} icon={XCircle} bg="bg-rose-50" text="text-rose-700" border="border-rose-200" />
      </div>

      {/* Role-specific Action */}
      {user.role === 'Manager' && (stats?.pending_manager_approvals || 0) > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm font-semibold text-amber-900">Action Required</p>
          <p className="text-xs text-amber-700 mt-1">
            You have {stats.pending_manager_approvals} salary approval(s) worth ₹{fmt(stats.pending_approval_amount)} pending your review
          </p>
        </div>
      )}

      {user.role === 'CEO' && (stats?.pending_ceo_approvals || 0) > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm font-semibold text-blue-900">Final Approval Pending</p>
          <p className="text-xs text-blue-700 mt-1">
            {stats.pending_ceo_approvals} salary process(es) worth ₹{fmt(stats.pending_ceo_amount)} awaiting your final approval
          </p>
        </div>
      )}

      {/* Month-wise Breakdown */}
      {monthly.length > 0 ? (
        <div className="space-y-4">
          {monthly.map(m => (
            <div key={m.month} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`month-card-${m.month}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Banknote className="h-5 w-5 text-indigo-600" />
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{monthLabel(m.month)}</h3>
                    <p className="text-xs text-slate-500">{m.total_processed} employee(s) processed</p>
                  </div>
                </div>
                <span className="text-lg font-bold font-mono text-slate-900" data-testid={`payroll-${m.month}`}>₹{fmt(m.total_net)}</span>
              </div>

              {/* Status-wise breakdown */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`draft-${m.month}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-3.5 w-3.5 text-slate-500" />
                    <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Draft</p>
                  </div>
                  <p className="text-lg font-bold font-mono text-slate-700">{m.draft_count}</p>
                  <p className="text-xs font-mono text-slate-500">₹{fmt(m.draft_amount)}</p>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`pending-mgr-${m.month}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-3.5 w-3.5 text-amber-600" />
                    <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wider">Pending Manager</p>
                  </div>
                  <p className="text-lg font-bold font-mono text-amber-700">{m.pending_manager}</p>
                  <p className="text-xs font-mono text-amber-600">₹{fmt(m.pending_manager_amount)}</p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`pending-ceo-${m.month}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5 text-blue-600" />
                    <p className="text-[10px] font-semibold text-blue-800 uppercase tracking-wider">Pending CEO</p>
                  </div>
                  <p className="text-lg font-bold font-mono text-blue-700">{m.pending_ceo}</p>
                  <p className="text-xs font-mono text-blue-600">₹{fmt(m.pending_ceo_amount)}</p>
                </div>
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`approved-${m.month}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    <p className="text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">Fully Approved</p>
                  </div>
                  <p className="text-lg font-bold font-mono text-emerald-700">{m.approved_count || 0}</p>
                  <p className="text-xs font-mono text-emerald-600">₹{fmt(m.approved_amount)}</p>
                </div>
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid={`pending-bank-${m.month}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Banknote className="h-3.5 w-3.5 text-indigo-600" />
                    <p className="text-[10px] font-semibold text-indigo-800 uppercase tracking-wider">Pending Banking</p>
                  </div>
                  <p className="text-lg font-bold font-mono text-indigo-700">{m.pending_payments}</p>
                  <p className="text-xs font-mono text-indigo-600">₹{fmt(m.pending_banking_amount)}</p>
                </div>
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid={`banking-done-${m.month}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-purple-600" />
                    <p className="text-[10px] font-semibold text-purple-800 uppercase tracking-wider">Banking Done</p>
                  </div>
                  <p className="text-lg font-bold font-mono text-purple-700">{m.frozen_count || 0}</p>
                  <p className="text-xs font-mono text-purple-600">₹{fmt(m.frozen_amount || 0)}</p>
                </div>
              </div>
              {m.rejected_count > 0 && (
                <div className="mt-3 p-2 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-2">
                  <XCircle className="h-3.5 w-3.5 text-rose-600" />
                  <p className="text-xs text-rose-700"><span className="font-semibold">{m.rejected_count} rejected</span></p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center shadow-sm">
          <Banknote className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No salary data processed yet</p>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, bg, text, border }) => (
  <div className={`${bg} border ${border} rounded-xl p-4 transition-all hover:shadow-md`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className={`text-xs font-medium uppercase tracking-wider ${text} opacity-70`}>{title}</p>
        <p className={`mt-2 text-3xl font-bold tracking-tight font-mono ${text}`}>{value}</p>
      </div>
      <div className={`${bg} p-2.5 rounded-lg border ${border}`}>
        <Icon className={`h-5 w-5 ${text}`} />
      </div>
    </div>
  </div>
);

export default Dashboard;
